import { apiClient, API_CONFIG } from '../config/api'
import { baliseService } from './baliseService'

const BM = API_CONFIG.BM_BASE_URL

// Brands the Balise Dashboard surfaces explicitly. Order is intentional —
// it matches the visual hierarchy on the page (legacy fleet first, new
// integrations after) and keeps JIMI visible even when there are zero
// JIMI rows so the operator never wonders "is JIMI online?".
//
// Add a new brand here when a new vendor lands in orchestration_db.
// The dashboard will not crash if a brand is removed; it just stops
// showing the card.
export const KNOWN_BALISE_BRANDS: { key: string; label: string }[] = [
  { key: 'MAXVISION', label: 'MaxVision' },
  { key: 'JOINTECH', label: 'Jointech' },
  { key: 'TELTONIKA', label: 'Teltonika' },
  { key: 'JIMI', label: 'JIMI' },
]

export interface BrandBreakdownEntry {
  /** Canonical brand key as stored in orchestration_balises.brand */
  brand: string
  /** Human-friendly label (the dashboard does not localize brand names) */
  label: string
  /** Total balises with this brand (server-side total, brand-filtered) */
  total: number
}

export interface DashboardStats {
  customers: { totalCustomers: number; activeCustomers: number }
  balises: {
    total_balises: number
    active_balises: number
    assigned_balises: number
    available_balises: number
    critical_balises: number
    low_battery_balises: number
    maintenance_due_balises: number
    unique_customers: number
    unique_brands: number
  }
  simCards: {
    total_sim_cards: number
    active_sim_cards: number
    assigned_sim_cards: number
    inactive_sim_cards: number
    suspended_sim_cards: number
    over_limit_sim_cards: number
    unique_operators: number
    total_monthly_cost: number
  }
  derived: { utilizationRate: number; balisesPerCustomer: number; simUtilizationRate: number }
  systemHealth: {
    baliseHealthScore: number
    simHealthScore: number
    overallHealthScore: number
    healthStatus: string
  }
  recentActivity: {
    recentBaliseUpdates: number
    recentCustomerChanges: number
    recentSimChanges: number
    recentSystemChanges: number
  }
  lastUpdated: string
}

export const dashboardBMService = {
  async getStatistics(): Promise<DashboardStats> {
    const { data } = await apiClient.get(`${BM}/dashboard/statistics`)
    return data
  },

  // The Java BM backend exposes recent activity at GET /dashboard/activity
  // (DashboardResource.java line 254). The endpoint returns four nested
  // buckets — baliseActivity, customerActivity, simActivity,
  // systemActivity — all currently empty arrays in the reference impl.
  // We flatten them into a single chronologically-sorted list so the UI
  // can render one feed and not care about source bucketing. Tag each
  // entry with its bucket so future styling can colour-code them.
  async getRecentActivity(): Promise<any[]> {
    try {
      const { data } = await apiClient.get(`${BM}/dashboard/activity`)
      const buckets: Array<[string, unknown]> = [
        ['balise',   data?.baliseActivity],
        ['customer', data?.customerActivity],
        ['sim',      data?.simActivity],
        ['system',   data?.systemActivity],
      ]
      const merged: any[] = []
      for (const [type, arr] of buckets) {
        if (Array.isArray(arr)) {
          for (const it of arr) {
            merged.push({ type, ...(it as Record<string, unknown>) })
          }
        }
      }
      merged.sort((a, b) => {
        const ta = a.timestamp ?? a.created_at ?? a.at ?? a.time ?? ''
        const tb = b.timestamp ?? b.created_at ?? b.at ?? b.time ?? ''
        return String(tb).localeCompare(String(ta))
      })
      return merged
    } catch {
      return []
    }
  },

  // Cheap per-brand totals computed by issuing parallel size=1 queries
  // to /api/bm/balises?brand=X. We only care about the `total` field
  // returned by the paginated envelope, so each request transfers one
  // row of payload at most. Brands that error out are returned as 0
  // rather than failing the whole dashboard — a brand integration that
  // is temporarily offline must not blank the screen for the operator.
  async getBrandBreakdown(): Promise<BrandBreakdownEntry[]> {
    const results = await Promise.all(
      KNOWN_BALISE_BRANDS.map(async ({ key, label }) => {
        try {
          const res = await baliseService.getAllBalises(1, 1, { brand: key })
          return { brand: key, label, total: res.total ?? 0 }
        } catch {
          return { brand: key, label, total: 0 }
        }
      }),
    )
    return results
  },
}
